import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { api, API_URL, resolveMediaUrl } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function BranchesContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    manager_id: '',
    working_hours: '',
    social_instagram: '',
    social_facebook: '',
    social_tiktok: '',
  });
  const [shopSocial, setShopSocial] = useState({ instagram: '', facebook: '', tiktok: '' });
  const [uploadingShopLogo, setUploadingShopLogo] = useState(false);
  const [uploadingBranchLogo, setUploadingBranchLogo] = useState(null);

  const shopId = user?.shop_id;
  const isShopManager = user?.role === 'shop_manager';

  const fetchBranches = async () => {
    if (!shopId) return;
    try { const data = await api.getBranches(null, shopId); setBranches(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (shopId) {
      fetchBranches();
      api.getShop(shopId).then((s) => {
        setShop(s);
        const sl = s?.social_links || {};
        setShopSocial({ instagram: sl.instagram || '', facebook: sl.facebook || '', tiktok: sl.tiktok || '' });
      }).catch(() => setShop(null));
    }
    if (isShopManager) api.getAssignableBranchManagers().then(setManagers).catch(() => setManagers([]));
  }, [shopId, isShopManager]);

  const onShopLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !shopId) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingShopLogo(true);
    try {
      const updated = await api.uploadShopLogo(shopId, formData);
      setShop(updated);
    } catch (err) { showAlert(err.message || t('admin.uploadFailed')); }
    finally { setUploadingShopLogo(false); e.target.value = ''; }
  };

  const onDeleteShopLogo = async () => {
    if (!shopId || !shop?.logo_url) return;
    showConfirm(t('admin.deleteLogoConfirm', 'Delete this logo?'), async () => {
      setUploadingShopLogo(true);
      try {
        const updated = await api.deleteShopLogo(shopId);
        setShop(updated);
      } catch (err) { showAlert(err.message || t('admin.uploadFailed')); }
      finally { setUploadingShopLogo(false); }
    });
  };

  const onBranchLogoSelect = async (branchId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingBranchLogo(branchId);
    try {
      await api.uploadBranchLogo(branchId, formData);
      fetchBranches();
    } catch (err) { showAlert(err.message || t('admin.uploadFailed')); }
    finally { setUploadingBranchLogo(null); e.target.value = ''; }
  };

  const onDeleteBranchLogo = async (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch?.logo_url) return;
    showConfirm(t('admin.deleteLogoConfirm', 'Delete this logo?'), async () => {
      setUploadingBranchLogo(branchId);
      try {
        await api.deleteBranchLogo(branchId);
        fetchBranches();
      } catch (err) { showAlert(err.message || t('admin.uploadFailed')); }
      finally { setUploadingBranchLogo(null); }
    });
  };

  const openCreate = () => {
    setEditing('create');
    setForm({ name: '', address: '', manager_id: '', working_hours: '', social_instagram: '', social_facebook: '', social_tiktok: '' });
  };
  const openEdit = (b) => {
    const msl = b.manager_social_links || {};
    setEditing(b.id);
    setForm({
      name: b.name,
      address: b.address,
      manager_id: b.manager_id ?? '',
      working_hours: b.working_hours || '',
      social_instagram: msl.instagram || '',
      social_facebook: msl.facebook || '',
      social_tiktok: msl.tiktok || '',
    });
  };

  const save = async () => {
    try {
      const manager_social_links = {};
      if (form.social_instagram?.trim()) manager_social_links.instagram = form.social_instagram.trim();
      if (form.social_facebook?.trim()) manager_social_links.facebook = form.social_facebook.trim();
      if (form.social_tiktok?.trim()) manager_social_links.tiktok = form.social_tiktok.trim();
      const wh = form.working_hours?.trim() ? form.working_hours.trim() : null;
      const mgr = form.manager_id ? parseInt(form.manager_id) : null;
      if (editing === 'create') {
        const createPayload = { shop_id: shopId, name: form.name, address: form.address, manager_id: mgr };
        if (wh) createPayload.working_hours = wh;
        if (Object.keys(manager_social_links).length) createPayload.manager_social_links = manager_social_links;
        await api.createBranch(createPayload);
      } else {
        await api.updateBranch(editing, {
          name: form.name,
          address: form.address,
          manager_id: mgr,
          working_hours: wh,
          manager_social_links,
        });
      }
      setEditing(null); fetchBranches();
    } catch (err) { showAlert(err.message || t('admin.failedSaveBranch')); }
  };

  const saveShopSocial = async () => {
    if (!shopId) return;
    try {
      const social_links = {};
      if (shopSocial.instagram?.trim()) social_links.instagram = shopSocial.instagram.trim();
      if (shopSocial.facebook?.trim()) social_links.facebook = shopSocial.facebook.trim();
      if (shopSocial.tiktok?.trim()) social_links.tiktok = shopSocial.tiktok.trim();
      const updated = await api.updateShop(shopId, { social_links });
      setShop(updated);
      showAlert('Saved');
    } catch (err) { showAlert(err.message || t('common.error')); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteBranchConfirm'), async () => {
      try { await api.deleteBranch(id); fetchBranches(); }
      catch (err) { showAlert(err.message || t('admin.failedDelete')); }
    });
  };

  if (!isShopManager) {
    return (
      <AdminLayout role="manager">
        <p style={{ color: T.muted }}>{t('manager.branchesShopManagersOnly')}</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout role="manager">
      {/* Shop logo (shop manager only) */}
      {shop && (
        <div style={{ ...S.card, marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: T.muted, marginBottom: '0.35rem' }}>{t('admin.shopLogo')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {shop.logo_url ? (
                <img src={resolveMediaUrl(shop.logo_url)} alt="" loading="lazy" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `1px solid ${T.border}` }} />
              ) : (
                <div style={{ width: 56, height: 56, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: T.muted }}>{t('admin.noLogo')}</div>
              )}
              <label style={{ cursor: 'pointer', fontSize: '0.8rem', color: T.orange }}>
                {uploadingShopLogo ? t('admin.uploading') : t('admin.uploadLogo')}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onShopLogoSelect} />
              </label>
              {shop.logo_url && (
                <button type="button" onClick={onDeleteShopLogo} style={{ ...S.btnDanger, padding: '0.35rem 0.55rem', fontSize: '0.72rem' }}>
                  {t('common.delete')}
                </button>
              )}
            </div>
          </div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.06em', color: T.white }}>{shop.name}</div>
        </div>
      )}

      {shop && (
        <div style={{ ...S.card, marginBottom: '2rem', padding: '1.25rem' }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: T.muted, marginBottom: '0.75rem' }}>{t('admin.salonSocialLinks')}</div>
          <input placeholder="Instagram URL" value={shopSocial.instagram} onChange={e => setShopSocial({ ...shopSocial, instagram: e.target.value })} style={{ ...S.input, marginBottom: '0.5rem' }} />
          <input placeholder="Facebook URL" value={shopSocial.facebook} onChange={e => setShopSocial({ ...shopSocial, facebook: e.target.value })} style={{ ...S.input, marginBottom: '0.5rem' }} />
          <input placeholder="TikTok URL" value={shopSocial.tiktok} onChange={e => setShopSocial({ ...shopSocial, tiktok: e.target.value })} style={{ ...S.input, marginBottom: '0.75rem' }} />
          <button type="button" onClick={saveShopSocial} style={S.btnOrange}>{t('common.save')}</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.branches')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addBranch')}</button>
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newBranch') : t('admin.editBranch')}</h2>
            <input placeholder={t('admin.branchName')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <input placeholder={t('admin.address')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={S.input} />
            <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.branchManagerOptional')}</label>
            <select value={form.manager_id} onChange={e => setForm({ ...form, manager_id: e.target.value })} style={S.select}>
              <option value="">{t('admin.none')}</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.email}){m.branch_id ? ` [${t('admin.assigned')}]` : ''}</option>)}
            </select>
            <input placeholder={t('admin.workingHoursOptional')} value={form.working_hours} onChange={e => setForm({ ...form, working_hours: e.target.value })} style={S.input} />
            <div style={{ fontSize: '0.72rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '0.35rem' }}>{t('admin.branchManagerSocialLinks')}</div>
            <input placeholder="Instagram URL" value={form.social_instagram} onChange={e => setForm({ ...form, social_instagram: e.target.value })} style={S.input} />
            <input placeholder="Facebook URL" value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value })} style={S.input} />
            <input placeholder="TikTok URL" value={form.social_tiktok} onChange={e => setForm({ ...form, social_tiktok: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.logo'), t('admin.name'), t('admin.address'), t('admin.manager'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {branches.map(b => (
                <tr key={b.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{b.id}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {b.logo_url ? (
                        <img src={resolveMediaUrl(b.logo_url)} alt="" loading="lazy" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: `1px solid ${T.border}` }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: T.muted }}>—</div>
                      )}
                      <label style={{ cursor: 'pointer', fontSize: '0.7rem', color: T.orange }}>
                        {uploadingBranchLogo === b.id ? '…' : t('admin.upload')}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onBranchLogoSelect(b.id, e)} />
                      </label>
                      {b.logo_url && (
                        <button type="button" onClick={() => onDeleteBranchLogo(b.id)} style={{ ...S.btnDanger, padding: '0.2rem 0.45rem', fontSize: '0.68rem' }}>
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={S.td}>{b.name}</td>
                  <td style={S.td}>{b.address}</td>
                  <td style={S.td}>{b.manager_name ?? '—'}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(b)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(b.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Branches() {
  return <ProtectedRoute requireSalonManager><BranchesContent /></ProtectedRoute>;
}
